/**
 * API response types
 */

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  requestId?: string;
}

// ============================================================================
// ERROR RESPONSE TYPES
// ============================================================================

/**
 * Service unavailability error details
 * Matches the error response structure from the API when service is unavailable
 * Example:
 * {
 *   "error": 503,
 *   "code": "SERVICE_UNAVAILABLE",
 *   "message": "Service temporarily unavailable",
 *   "details": {
 *     "service_status": "UNAVAILABLE",
 *     "reads_allowed": false,
 *     "writes_allowed": false,
 *     "streams_allowed": false,
 *     "circuit_breaker_state": "OPEN",
 *     "retry_after_seconds": 30,
 *     "request_type": "read"
 *   },
 *   "timestamp": 1769317535
 * }
 */
/** 503 details (camelCase). Normalize wire snake_case before typing. */
export interface ServiceUnavailableErrorDetails {
  serviceStatus?: "UNAVAILABLE" | "DEGRADED" | "AVAILABLE";
  readsAllowed?: boolean;
  writesAllowed?: boolean;
  streamsAllowed?: boolean;
  circuitBreakerState?: "OPEN" | "CLOSED" | "HALF_OPEN";
  retryAfterSeconds?: number;
  requestType?: "read" | "write" | "stream" | string;
  service?: string;
  reason?: string;
  [key: string]: any;
}

/**
 * Service unavailability error response
 * HTTP 503 error response structure from the API
 */
export interface ServiceUnavailableError {
  error: number; // HTTP status code (e.g., 503)
  code: string; // Error code (e.g., "SERVICE_UNAVAILABLE")
  message: string; // Human-readable error message
  details: ServiceUnavailableErrorDetails;
  timestamp: number; // Unix timestamp
  [key: string]: any; // Allow additional fields
}

/**
 * Generic API error response structure per error_responses.md.
 * Standard: error (HTTP status), message; some include code, status, success, txhash.
 */
export interface ApiErrorResponse {
  error?: number | string; // HTTP status code or error identifier
  code?: string; // Error code (e.g. invalid_address_format)
  message?: string; // Human-readable error message
  status?: number; // HTTP status when present (e.g. 400)
  success?: boolean; // Transaction/signature endpoints set false on error
  txhash?: string; // Transaction endpoints include on error
  details?: Record<string, any>; // Additional error details (e.g. 503 details)
  timestamp?: number; // Unix timestamp
  [key: string]: any; // Allow additional fields
}

/** Callback for subscription data. Payload shape depends on channel/stream; use type guards or narrow in handler. */
export type SubscriptionCallback = (data: unknown) => void;

// ============================================================================
// BUCKET TYPES
// ============================================================================

/**
 * Raw bucket data (camelCase only). Matches buckets API response shape.
 */
export interface RawBucket {
  bucketId: string;
  address: string;
  bucketType: string;
  collateralAssetIndex: string;
  depositedMargin: string;
  totalEquity: string;
  availableMargin: string;
  usedMargin: string;
  unrealizedProfit: string;
  unrealizedLoss: string;
  realizedProfit: string;
  realizedLoss: string;
  marginRatio: string;
  imr: string;
  totalNotionalValue: string;
  riskLevel: string;
  maxLeverage: string;
  correlationOffset: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  sequenceId: string;
}

/**
 * WebSocket buckets update message (camelCase only).
 */
export interface BucketsUpdateMessage {
  buckets: RawBucket[];
  totalBuckets?: number;
  address: string;
  timestamp: number;
  [key: string]: any;
}

// ============================================================================
// MARKET TYPES (query market API – camelCase, one source of truth)
// ============================================================================

/**
 * Single market from the market query API.
 * Matches response shape: baseAssetIndex, marketIndex, marketType, name, orderbookType, quoteAssetIndex, status, symbol.
 */
export interface Market {
  baseAssetIndex: string;
  marketIndex: string;
  marketType: string;
  name: string;
  orderbookType: string;
  quoteAssetIndex: string;
  status: string;
  symbol: string;
}

/**
 * Response from GET markets (list) endpoint per market_api.md.
 * Top-level: count, defaultMarketId, defaultMarket, markets. SDK normalizes to total (count ?? total).
 */
export interface GetMarketsResponse {
  markets: Market[];
  /** Total number of markets; may be derived from count (API) or total (legacy). */
  total?: number;
  /** API returns count; SDK may map to total. */
  count?: number;
  defaultMarketId?: string;
  defaultMarket?: Market;
}

/**
 * Market statistics/ticker data (proto camelCase).
 */
export interface RawMarketStats {
  marketIndex: string;
  symbol: string;
  moniker: string;
  lastPrice: string;
  bidPrice: string;
  askPrice: string;
  volume?: string;
  high24h?: string;
  low24h?: string;
  priceChange24h?: string;
  priceChangePercent24h?: string;
  timestamp?: number;
}

/**
 * Exchange info response
 */
export interface ExchangeInfoResponse {
  timezone?: string;
  serverTime?: number;
  rateLimits?: Array<{
    rateLimitType?: string;
    interval?: string;
    intervalNum?: number;
    limit?: number;
  }>;
  exchangeFilters?: any[];
  symbols?: any[];
  [key: string]: any;
}

// ============================================================================
// ORDERBOOK TYPES
// ============================================================================

/**
 * Orderbook entry [price, size] — normalized tuple format for downstream consumers.
 * Can be strings (raw satoshi format) or numbers (converted decimal format).
 */
export type OrderbookEntry = [string | number, string | number];

/**
 * PriceLevel from proto QueryOrderbookSnapshotResponse (primitives.v1.PriceLevel).
 * This is the raw JSON format returned by the /clob/v1/orderbook/{marketIndex} endpoint.
 */
export interface PriceLevel {
  price: string;
  quantity: string;
  orderCount?: number;
}

/**
 * Orderbook response (proto camelCase).
 * Matches QueryOrderbookSnapshotResponse from proto-lib/proto/clob/v1/query.proto.
 * bids/asks are normalized to OrderbookEntry tuples by InfoClient.getOrderbook().
 */
export interface RawOrderbook {
  marketIndex?: string;
  /** Market symbol (e.g., "BTC-USDC-PERP") from proto response. */
  symbol?: string;
  bids: OrderbookEntry[];
  asks: OrderbookEntry[];
  lastUpdateId?: number;
  timestamp?: number;
  /** Sequence ID for ordering; E2E-verified in orderbook snapshot. */
  sequenceId?: number;
  /** CRC32 checksum for integrity (orderbook resync). */
  checksum?: string;
  /** Block height for L1-specific determinism. */
  blockHeight?: string;
  /** Last update timestamp from proto response. */
  updatedAt?: string;
}

// ============================================================================
// TRADE TYPES
// ============================================================================

/**
 * Raw trade data from API (proto camelCase).
 */
export interface RawTrade {
  tradeId: string;
  buyOrderId: string;
  sellOrderId: string;
  price: string; // Satoshi format
  quantity: string; // Satoshi format
  timestamp: string; // Timestamp as string
  marketIndex: string;
  makerFee: string; // Satoshi format
  takerFee: string; // Satoshi format
  dualWriteId: string;
  sequenceId: string;
  tradeType: string; // e.g., "INITIAL"
  buyAddress: string;
  sellAddress: string;
}

/**
 * Trades response structure from API
 * Matches actual REST API response format from /clob/v1/trades endpoint
 */
export interface TradesResponse {
  trades: RawTrade[];
  totalCount: number;
  marketIndex: string;
  symbol: string;
  timestamp: Timestamp; // Protobuf Timestamp object { seconds, nanos }
}

/**
 * Aggregate trade data
 */
export interface RawAggTrade {
  symbol: string;
  price: string | number;
  quantity: string | number;
  firstTradeId?: string | number;
  lastTradeId?: string | number;
  timestamp: number;
  [key: string]: any;
}

// ============================================================================
// KLINE/CANDLESTICK TYPES
// ============================================================================

/**
 * Kline/candlestick data
 * Format: [openTime, open, high, low, close, volume, closeTime, quoteVolume, trades, ...]
 */
export type RawKline = [
  number, // openTime
  string | number, // open
  string | number, // high
  string | number, // low
  string | number, // close
  string | number, // volume
  number, // closeTime
  string, // quoteVolume
  number?, // trades
  ...any[],
];

/**
 * WebSocket kline data structure (proto camelCase).
 * Matches the actual WebSocket message format with protobuf timestamps.
 */
export interface WebSocketKlineData {
  marketIndex: string;
  symbol?: string;
  interval: string;
  open: string; // Satoshi format
  high: string; // Satoshi format
  low: string; // Satoshi format
  close: string; // Satoshi format
  volume: string; // Satoshi format
  tradeCount?: number;
  openTime: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  closeTime: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  timestamp: Timestamp | number; // Protobuf timestamp { seconds, nanos } or number
  [key: string]: any; // Allow additional fields
}

/**
 * Klines response (array of klines)
 */
export type KlinesResponse = RawKline[];

// ============================================================================
// POSITION TYPES
// ============================================================================

/**
 * Raw position data from API (proto camelCase).
 * Matches actual API response structure from /bucket/v1/address/{address}/positions.
 */
export interface RawPosition {
  positionId: string; // Position identifier
  bucketId: string;
  marketIndex: string;
  address: string; // Wallet address
  symbol: string;
  size: string; // Satoshi format
  side: string; // Position side: "SIDE_BUY" (long) or "SIDE_SELL" (short)
  averageEntryPrice: string; // Satoshi format (average entry price)
  markPrice: string; // Satoshi format
  leverage: string; // Satoshi format (e.g., "200000000" = 2x)
  unrealizedProfit: string; // Satoshi format
  unrealizedLoss: string; // Satoshi format
  mmr: string; // Maintenance margin requirement (Satoshi format)
  marginBalance: string; // Satoshi format
  status: string; // Position status (e.g., "active")
  createdAt: Timestamp; // ISO timestamp string
  updatedAt: Timestamp; // ISO timestamp string
  unrealizedPnl?: string; // Legacy field (calculate from unrealizedProfit - unrealizedLoss)
  liquidationPrice?: string; // Satoshi format (optional, not always in response)
  marketType?: string; // Market type (optional, not always in response)
  marginMode?: string;
  marginType?: string;
}

/**
 * Positions response structure from API
 */
/** Proto QueryPositionsByAddressResponse (camelCase). Optional shardId if gateway adds it. */
export interface PositionsResponse {
  success: boolean;
  errorMessage: string;
  positions: RawPosition[];
  totalCount: number;
  filteredCount: number;
  wasFiltered: boolean;
  shardId?: string;
}

// ============================================================================
// ORDER TYPES
// ============================================================================

/**
 * Order side union - strict types from API
 */
export type OrderSide = "SIDE_BUY" | "SIDE_SELL";

/**
 * Order type union - strict types from API
 */
export type OrderType =
  | "ORDER_TYPE_UNSPECIFIED"
  | "ORDER_TYPE_LIMIT"
  | "ORDER_TYPE_MARKET"
  | "ORDER_TYPE_STOP_LIMIT"
  | "ORDER_TYPE_STOP_MARKET";

/**
 * Order status union - strict types from API
 */
export type OrderStatus =
  | "ORDER_STATUS_OPEN"
  | "ORDER_STATUS_ACTIVE"
  | "ORDER_STATUS_FILLED"
  | "ORDER_STATUS_CANCELLED"
  | "ORDER_STATUS_PARTIAL"
  | "ORDER_STATUS_PENDING";

/**
 * Raw order data from API. Proto canonical camelCase (clob/v1/clob.proto Order).
 * marketIndex when present is always string.
 */
export interface RawOrder {
  orderId?: string;
  marketIndex?: string;
  bucketId?: string;
  address: string;
  side: OrderSide;
  type: OrderType;
  category?: string; // e.g., "MARKET_CATEGORY_LINEAR"
  price: string; // Satoshi format
  quantity: string; // Satoshi format
  status: OrderStatus; // Order status from API
  power?: number;
  leverage?: string;
  filledQuantity?: string; // Satoshi format
  averagePrice?: string; // Satoshi format
  takeProfit?: string; // Can be empty string
  stopLoss?: string; // Can be empty string
  /** API may return snake_case; SDK accepts both */
  stop_loss?: string;
  take_profit?: string;
  timestamp?: Timestamp | string | number;
  timeInForce?: string; // e.g., "TIME_IN_FORCE_GTC"
  postOnly?: boolean;
  hidden?: boolean;
  displayQuantity?: string; // Can be empty string
  dualWriteId?: string;
  sequenceId?: string;
  [key: string]: any;
}

/**
 * Orders response (QueryOrdersByAddressResponse / QueryOrdersByMarketResponse). Proto camelCase.
 */
export interface OrdersResponse {
  orders: RawOrder[];
  totalCount: number;
  timestamp: Timestamp | string | number;
  paginationResponse?: { offset?: string; limit?: string; total?: string };
  [key: string]: any;
}

// ============================================================================
// BALANCE TYPES
// ============================================================================

/**
 * Asset object from bank balance API (proto camelCase).
 * Matches GET /bank/v1/balances/{address} response: each balance has asset as object.
 */
export interface BalanceAsset {
  assetIndex: string;
  bridgeId?: string;
  isNative?: boolean;
  metadata?: unknown;
  symbol: string;
  type?: string; // e.g. "ASSET_TYPE_NATIVE_MORM", "ASSET_TYPE_TOKEN"
  uniqueKey?: string;
}

/**
 * Raw balance (camelCase only). Normalize wire response before typing.
 * API returns asset as object (BalanceAsset); SDK normalizes to asset: symbol string for backward compat.
 */
export interface RawBalance {
  /** Symbol string (e.g. "MORM", "USDC"). Normalized from asset.symbol when API returns asset as object. */
  asset?: string;
  /** Legacy: some APIs return collateralAssetIndex.ticker */
  collateralAssetIndex?: { ticker?: string };
  balance: string;
  availableBalance?: string;
  lockedBalance?: string;
  /** Per-balance shardId when present */
  shardId?: string;
  [key: string]: any;
}

/**
 * Get balances response (camelCase only).
 * Matches GET /bank/v1/balances/{address} response structure.
 */
export interface GetBalancesResponse {
  address: string;
  balances: RawBalance[];
  shardId?: string;
}

// ============================================================================
// FUNDING RATE TYPES
// ============================================================================

/**
 * Raw funding rate data
 */
export interface RawFundingRate {
  symbol?: string;
  fundingRate?: string | number;
  fundingTime?: number;
  markPrice?: string | number;
  [key: string]: any;
}

/**
 * Funding rate response
 */
export type FundingRateResponse =
  | RawFundingRate
  | RawFundingRate[]
  | { data?: RawFundingRate[] };

/**
 * Premium index data
 */
export interface RawPremiumIndex {
  symbol?: string;
  markPrice?: string | number;
  indexPrice?: string | number;
  estimatedSettlePrice?: string | number;
  lastFundingRate?: string | number;
  nextFundingTime?: number;
  interestRate?: string | number;
  [key: string]: any;
}

// ============================================================================
// HEALTH & METRICS TYPES
// ============================================================================

/**
 * Health summary response
 */
export interface HealthSummaryResponse {
  status?: string;
  overall?: string;
  components?: Record<
    string,
    {
      status?: string;
      message?: string;
      [key: string]: any;
    }
  >;
  timestamp: Timestamp | number;
  [key: string]: any;
}

/**
 * Nonce status response from /router/v1/nonce/status/{address} per nonce_api.
 */
export interface NonceStatusResponse {
  address: string;
  nextNonce: number;
  reserved: boolean;
  timestamp: { seconds: string; nanos: number };
}

/**
 * Server time response
 */
export interface ServerTimeResponse {
  serverTime?: number;
  time?: number;
  timestamp?: number;
  [key: string]: any;
}

/**
 * TPS metrics response
 */
export interface TPSMetricsResponse {
  current?: number;
  peak?: number;
  average?: number;
  [key: string]: any;
}

/**
 * Latency metrics response
 */
export interface LatencyMetricsResponse {
  p50?: number;
  p95?: number;
  p99?: number;
  avg?: number;
  max?: number;
  min?: number;
  [key: string]: any;
}

// ============================================================================
// OTHER TYPES
// ============================================================================

/**
 * Open interest response
 */
export interface OpenInterestResponse {
  symbol?: string;
  openInterest?: string | number;
  [key: string]: any;
}

/**
 * TWAP response
 */
export interface TWAPResponse {
  symbol?: string;
  twap?: string | number;
  duration?: number;
  [key: string]: any;
}

/**
 * Index info response
 */
export interface IndexInfoResponse {
  symbol?: string;
  indexPrice?: string | number;
  [key: string]: any;
}

/**
 * Asset index response
 */
export interface AssetIndexResponse {
  symbol?: string;
  assetIndex?: string | number;
  [key: string]: any;
}

/**
 * Constituents response
 */
export interface ConstituentsResponse {
  symbol?: string;
  constituents?: Array<{
    asset?: string;
    weight?: string | number;
    [key: string]: any;
  }>;
  [key: string]: any;
}

/**
 * Delivery price response
 */
export interface DeliveryPriceResponse {
  pair?: string;
  deliveryPrice?: string | number;
  delivery_price?: string | number;
  [key: string]: any;
}

/**
 * Basis data response
 */
export interface BasisResponse {
  symbol?: string;
  basis?: string | number;
  [key: string]: any;
}

/**
 * Long/short position ratio response
 */
export interface LongShortRatioResponse {
  symbol?: string;
  longShortRatio?: string | number;
  [key: string]: any;
}

/**
 * Supported actions response
 */
export interface SupportedActionsResponse {
  actions?: string[];
  [key: string]: any;
}

/**
 * Validate action response
 */
export interface ValidateActionResponse {
  valid: boolean;
  action: string;
  message: string;
  [key: string]: any;
}

/**
 * Market data response
 */
export interface MarketDataResponse {
  assetPair?: string;
  [key: string]: any;
}

/**
 * Market candles response
 */
export type MarketCandlesResponse = RawKline[];

// ============================================================================
// EXCHANGE CLIENT RESPONSE TYPES
// ============================================================================

/**
 * Place order response from /clob/v1/order or from POST /tx/v1/submit (place order).
 * Proto canonical camelCase.
 */
export interface PlaceOrderResponse {
  orderId?: string;
  clientOrderId?: string;
  timestamp?: number | string;
  errorMessage?: string;
  success: boolean;
  txhash?: string;
  accepted?: boolean;
}

/**
 * Protobuf-style timestamp structure
 */
export interface Timestamp {
  seconds: number;
  nanos: number;
}

/**
 * Cancel order result structure (proto camelCase).
 * The result field in cancelOrder response.
 */
export interface CancelOrderResult {
  orderId: string;
  success: boolean;
  timestamp: Timestamp;
}

/**
 * Fixed response type from POST /tx/v1/submit (tx ingress).
 * All fields are required per API contract — no optional guessing.
 * Example: {"accepted":true, "txhash":"...", "errorMessage":"", "shardId":"shard-0", "vrf_verified":true}
 * Commit nonce only when accepted is true.
 */
export interface TxV1SubmitResponse {
  accepted: boolean;
  txhash: string;
  errorMessage: string;
  shardId: string;
  vrf_verified: boolean;
}

/**
 * Parse raw HTTP body into TxV1SubmitResponse.
 * Handles wire variants: txhash/tx_hash/Txhash, vrf_verified/vrfVerified.
 */
export function parseTxV1SubmitResponse(body: unknown): TxV1SubmitResponse {
  const b = (body ?? {}) as Record<string, unknown>;
  const txhash =
    (b.txhash != null ? String(b.txhash) : null) ??
    (b.tx_hash != null ? String(b.tx_hash) : null) ??
    (b.Txhash != null ? String(b.Txhash) : null) ??
    "";
  const vrfVerified =
    b.vrf_verified === true || b.vrfVerified === true;
  return {
    accepted: b.accepted === true,
    txhash,
    errorMessage: b.errorMessage != null ? String(b.errorMessage) : "",
    shardId: b.shardId != null ? String(b.shardId) : "",
    vrf_verified: vrfVerified,
  };
}

/**
 * Router transaction response from /router/v1/tx endpoint
 * Common response structure for all router v1 transaction operations
 */
export interface RouterTxResponse {
  txhash: string;
  status?: string;
  message?: string;
  success?: boolean;
  error?: string;
  data?: any;
  result?: any; // Operation-specific result data
  timestamp?: Timestamp| number; // Top-level timestamp
  [key: string]: any;
}

/**
 * Cancel order response (proto camelCase).
 * Response from cancelOrder operation (POST /tx/v1/submit; accepted, txhash, errorMessage).
 * Structure: { success, txhash, result: { orderId, success, timestamp }, timestamp }
 */
export interface CancelOrderResponse extends RouterTxResponse {
  success: boolean;
  txhash: string;
  result: CancelOrderResult;
  timestamp: Timestamp | number;
}

/**
 * Modify order response
 * Response from modifyOrder operation (uses /router/v1/tx)
 */
export type ModifyOrderResponse = RouterTxResponse;

/**
 * Change leverage response
 * Response from changeLeverage operation (uses /router/v1/tx)
 */
export type ChangeLeverageResponse = RouterTxResponse;

export interface ClosePositionResponse extends RouterTxResponse {
  success: boolean;
  errorMessage?: string;
  error?: string;
  message?: string;
}

/**
 * Swap response
 * Response from swap operation (uses /router/v1/tx)
 */
export type SwapResponse = RouterTxResponse;

/**
 * Change margin ratio response
 * Response from changeMarginRatio operation (uses /router/v1/tx)
 */
export type ChangeMarginRatioResponse = RouterTxResponse;

/**
 * Internal transfer response
 * Response from internTransfer operation (uses /router/v1/tx)
 */
export type InternTransferResponse = RouterTxResponse;

/**
 * Bank mint response
 * Response from bankMint operation (uses /router/v1/tx)
 */
export type BankMintResponse = RouterTxResponse;

/**
 * Bank transfer response
 * Response from bankTransfer operation (uses /router/v1/tx)
 */
export type BankTransferResponse = RouterTxResponse;

/**
 * Bucket transfer response from /bucket/v1/bucket/transfer endpoint
 * Response structure for bucket-to-bucket transfers
 */
/** Proto TransferBetweenBucketsResponse (bucket/v1/tx.proto). CamelCase only. */
export interface BucketTransferResponse {
  success: boolean;
  message: string;
  transferredAmount: string;
  timestamp: Timestamp | string;
  sourceBucketId: string;
  targetBucketId: string;
  sourceBalanceAfter: string;
  targetBalanceAfter: string;
  txhash?: string;
  [key: string]: any;
}

/**
 * Extract to bucket response from /bank/v1/extract-to-bucket endpoint
 * Response structure for bank-to-bucket transfers
 */
export interface ExtractToBucketResponse {
  success: boolean;
  errorMessage?: string;
  txhash?: string;
  bucketId?: string;
  amountTransferred?: string;
  spotBalanceAfter?: string;
  bucketMarginAfter?: string;
  timestamp: Timestamp | string;
  [key: string]: any;
}

/**
 * Withdraw from bucket response from /bank/v1/withdraw-from-bucket endpoint
 * Response structure for bucket-to-bank transfers
 */
export interface WithdrawFromBucketResponse {
  success: boolean;
  errorMessage?: string;
  txhash?: string;
  bucketId?: string;
  amountTransferred?: string;
  spotBalanceAfter?: string;
  bucketMarginAfter?: string;
  timestamp: Timestamp | string;
  [key: string]: any;
}

/**
 * Create bucket response from /bucket/v1/bucket endpoint
 * Response structure for bucket creation
 */
export interface CreateBucketResponse {
  success: boolean;
  bucketId?: string;
  message: string;
  [key: string]: any;
}

// ============================================================================
// CONSENSUS FLAT TYPES
// ============================================================================

/**
 * Consensus item type union
 */
export type ConsensusItemType =
  | "vertex"
  | "tx"
  | "validator"
  | "unconfirmed_tx";

/**
 * Consensus event type union
 */
export type ConsensusEventType =
  | "new_vertex"
  | "new_tx"
  | "validator_update"
  | string;

/**
 * Common fields for all consensus item types (ConsensusCommonItemFlat)
 * These fields are present in all consensus items regardless of type.
 */
export interface ConsensusCommonItemFlat {
  id: string;
  type: ConsensusItemType;
  shardId: number;
  timestamp: number;
  status: string;
  originalProtoHash: string;
}

/**
 * Vertex-specific consensus item (ConsensusVertexItemFlat)
 * Represents a DAG vertex/block in the consensus system.
 */
export interface ConsensusVertexItemFlat extends ConsensusCommonItemFlat {
  type: "vertex";
  parentIds?: string[];
  txCount?: number;
  stateRoot?: string;
  qcAchieved?: boolean;
  qcVoteCount?: number;
  qcAggSignature?: string;
}

/**
 * Transaction-specific consensus item (ConsensusTxItemFlat)
 * Represents a confirmed transaction in the consensus system.
 */
export interface ConsensusTxItemFlat extends ConsensusCommonItemFlat {
  type: "tx";
  fromAddress?: string;
  toAddress?: string;
  amount?: string;
  txType?: string;
  txHash?: string;
  nonce?: number;
}

/**
 * Unconfirmed transaction-specific consensus item (ConsensusUnconfirmedTxItemFlat)
 * Represents an unconfirmed transaction in the consensus system.
 */
export interface ConsensusUnconfirmedTxItemFlat
  extends ConsensusCommonItemFlat {
  type: "unconfirmed_tx";
  fromAddress?: string;
  toAddress?: string;
  amount?: string;
  txType?: string;
  txHash?: string;
  nonce?: number;
}

/**
 * Validator-specific consensus item (ConsensusValidatorItemFlat)
 * Represents a validator in the consensus system.
 */
export interface ConsensusValidatorItemFlat extends ConsensusCommonItemFlat {
  type: "validator";
  votingPower?: number;
  publicKey?: string;
  validatorStatus?: string;
  validatorId?: string;
}

/**
 * Union type for all consensus item types (ConsensusItemFlat)
 * This is a discriminated union based on the `type` field, allowing TypeScript
 * to narrow the type based on the `type` property.
 */
export type ConsensusItemFlat =
  | ConsensusVertexItemFlat
  | ConsensusTxItemFlat
  | ConsensusUnconfirmedTxItemFlat
  | ConsensusValidatorItemFlat;

/**
 * Consensus event flat structure (ConsensusEventFlat)
 * Flattened (linearized) consensus events from Morpheum's DAG-based consensus system.
 * This is designed for easier consumption by block explorers and clients, converting
 * complex DAG structures into a simpler, block-like event stream.
 *
 * The payload follows the `ConsensusEventFlat` Go struct (from `services/consensus/types.go`),
 * which is marshaled to JSON using `protojson` (snake_case fields, no zero values emitted).
 */
export interface ConsensusEventFlat {
  eventType: ConsensusEventType;
  data: ConsensusItemFlat;
  sourceShard: string;
  eventTime: number;
}

// ============================================================================
// TRANSACTION EVENT TYPES
// Based on consensus_tx_test.go WebSocket transaction streaming structure
// ============================================================================

/**
 * Transaction event data structure
 * Represents transaction data nested inside a ConsensusEvent from the transactions channel.
 * Structure matches the Go test expectations: { hash, txhash, ... }
 */
export interface TransactionEventData {
  hash?: string;
  txHash?: string;
  vertexHash?: string;
  transactionIndex?: number;
  dagDepth?: number;
  shardId?: number;
  consensusRound?: number;
  code?: number;
  rawLog?: string;
  info?: string;
  data?: string;
  status?: string;
  timestamp?: Timestamp | number;
  consensusVersion?: string;
  parentVertices?: string[];
  [key: string]: any;
}

/**
 * Transaction event structure from transactions WebSocket channel
 * Matches the structure expected by consensus_tx_test.go:
 * { event_type, data: { hash, txhash, ... }, source_shard, event_time }
 */
export interface TransactionEvent {
  eventType: string;
  data: TransactionEventData;
  sourceShard?: string;
  eventTime?: number | Timestamp;
  [key: string]: any;
}

// ============================================================================
// STREAM MESSAGE TYPES (multiplex retention / callbacks)
// Used when building retained or callback payloads from multiplex stream data.
// ============================================================================

/**
 * Envelope for a single stream message (multiplex). Used for retention and
 * callback shapes; channel is "multiplex" since only multiplex API is supported.
 */
export interface StreamMessageEnvelope {
  type: "data";
  channel: "multiplex";
  /** Stream type: orderbook, trades, positions, markPrice, priceFeed, etc. */
  subChannel?: string;
  marketIndex?: string;
  id?: string;
  data: unknown;
  firstSequenceId?: number;
  lastSequenceId?: number;
  sequenceId?: number;
  checksum?: string;
}

/** Retained/callback message (envelope + data). */
export interface StreamMessage extends StreamMessageEnvelope {
  data: unknown;
}

/**
 * Legacy ack type (multiplex does not use this shape; kept for type compatibility).
 */
export interface MultiFeedAck {
  type: "ack";
  channel: "multiplex";
  id?: string;
  marketIndex: string;
  symbol: string;
  address?: string;
  message: string;
  subChannels: string[];
}

/**
 * Multi-feed orderbook entry (tuple format: [price, quantity])
 * Note: Uses tuple format, not object format
 */
export type MultiFeedOrderbookEntry = [string, string];

/**
 * Multi-feed orderbook data structure (proto camelCase). marketIndex is always string.
 */
export interface MultiFeedOrderbookData {
  marketIndex: string;
  sequenceId: string;
  firstUpdateId: number;
  lastUpdateId: number;
  checksum: string;
  bids: MultiFeedOrderbookEntry[];
  asks: MultiFeedOrderbookEntry[];
  timestamp: string;
  snapshot?: boolean;
}

/**
 * Multi-feed trades data structure (proto camelCase).
 */
export interface MultiFeedTradesData {
  tradeId: string;
  price: string;
  quantity: string;
  side: "BUY" | "SELL";
  executedAt: Timestamp;
  timestamp: Timestamp;
}

/**
 * Multi-feed positions data structure (authenticated, proto camelCase).
 * Wire: position_side → positionSide; marketType values "linear_perp" | "inverse_perp" | "spot".
 */
export interface MultiFeedPositionsData {
  positionId?: string;
  marketIndex: string;
  address: string;
  symbol: string;
  bucketId: string;
  size: string;
  entryPrice: string;
  markPrice: string;
  unrealizedProfit: string;
  unrealizedLoss: string;
  marginRatio: string;
  leverage: string;
  liquidationPrice: string;
  timestamp: Timestamp;
  status: string;
  mmr: string;
  marginBalance: string;
  marketType: "linear_perp" | "inverse_perp" | "spot" | string;
  realizedProfit: string;
  realizedLoss: string;
  /** Position side (wire: position_side). */
  positionSide: "LONG" | "SHORT";
  sequenceId?: number;
}

/**
 * Multi-feed orders data structure (authenticated, proto camelCase).
 */
export interface MultiFeedOrdersData {
  address: string;
  orderId: string;
  marketIndex: string;
  bucketId: string;
  symbol: string;
  status: string; // "PENDING", "FILLED", "CANCELLED", etc.
  filledQuantity: string;
  remainingQuantity: string;
  averageFillPrice: string;
  reason: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed buckets data structure (authenticated)
 */
export interface MultiFeedBucketsData {
  address: string;
  buckets: MultiFeedBucketData[];
  totalBuckets?: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed bucket data structure (proto camelCase).
 */
export interface MultiFeedBucketData {
  bucketId: string;
  address: string;
  marginType: "ISOLATED" | "CROSS";
  totalEquity: string;
  usedMargin: string;
  availableMargin: string;
  unrealizedProfit: string;
  unrealizedLoss: string;
}

/**
 * Multi-feed balances data structure (authenticated)
 */
export interface MultiFeedBalancesData {
  address: string;
  balances: MultiFeedBalanceData[];
  count: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed balance data structure
 */
export interface MultiFeedBalanceData {
  asset: string;
  balance: string;
  availableBalance?: string;
  lockedBalance?: string;
}

/**
 * Multi-feed mark price data structure
 */
export interface MultiFeedMarkPriceData {
  symbol: string;
  markPrice?: string;
  indexPrice?: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed funding rate data structure (proto camelCase).
 * Wire: open_interest_long, open_interest_short, long_short_ratio, longPercentage, shortPercentage (ratioScale 1e8).
 */
export interface MultiFeedFundingRateData {
  symbol: string;
  marketIndex: string;
  fundingRate: string;
  markPrice: string;
  indexPrice: string;
  premiumRate: string;
  /** Total long open interest (wire: open_interest_long, satoshi/ratioScale). */
  openInterestLong?: string;
  /** Total short open interest (wire: open_interest_short, satoshi/ratioScale). */
  openInterestShort?: string;
  /** Long/short ratio (wire: long_short_ratio; 1.5 = "150000000"). */
  longShortRatio?: string;
  longPercentage?: string;
  shortPercentage?: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed deviation data structure (proto camelCase).
 * Wire: deviation_percent → deviationPercent.
 */
export interface MultiFeedDeviationData {
  symbol: string;
  marketIndex: string;
  deviationBps: string;
  deviationPercent: string;
  lastPrice: string;
  markPrice: string;
  indexPrice: string;
  direction: "contango" | "backwardation" | "par";
  timestamp: Timestamp;
}

/**
 * Multi-feed TWAP data structure (proto camelCase).
 * Wire: twap_value → twapValue, calculation_method → calculationMethod (value e.g. "time_weighted").
 */
export interface MultiFeedTWAPData {
  symbol: string;
  marketIndex: string;
  twapValue: string;
  windowSize: number;
  sampleCount: number;
  startTime: number;
  endTime: number;
  /** Calculation method (e.g. "time_weighted"); not a request parameter. */
  calculationMethod: string;
  timestamp: Timestamp;
}

/**
 * Multi-feed kline data structure (proto camelCase).
 */
export interface MultiFeedKlineData {
  symbol: string;
  interval: string; // e.g., "1m", "5m", "1h"
  openTime: number; // Unix timestamp
  closeTime: number; // Unix timestamp
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  tradeCount: number;
  timestamp: Timestamp;
}

/**
 * Multi-feed last price data structure (proto camelCase).
 */
export interface MultiFeedLastPriceData {
  symbol: string;
  marketIndex: string;
  price: string;
  quantity: string;
  isBuyerMaker: boolean;
  tradeId: string;
  timestamp: Timestamp;
  derived: boolean; // true if derived from mark price (no trades exist)
}

/**
 * Multi-feed index price data structure (proto camelCase).
 * Wire: weighted_average, weighted_median, is_valid → weightedAverage, weightedMedian, isValid.
 */
export interface MultiFeedIndexPriceData {
  symbol: string;
  /** Wire: weighted_average. */
  weightedAverage?: string;
  /** Wire: weighted_median. */
  weightedMedian?: string;
  confidence: string;
  totalSources?: number;
  reliableSources?: number;
  /** Wire: is_valid. */
  isValid?: boolean;
  timestamp: Timestamp;
}

/**
 * Multi-feed price feed data structure (proto camelCase).
 * Wire: source_exchange → sourceExchange; wire often sends ticker (e.g. "BTC/USD"). Use symbol ?? ticker for display.
 */
export interface MultiFeedPriceFeedData {
  symbol?: string;
  ticker?: string;
  price: string;
  volume24h?: string;
  priceChange24h?: string;
  /** Source exchange (wire: source_exchange), e.g. "Pyth", "Chainlink". */
  sourceExchange?: string;
  timestamp: Timestamp;
}

/**
 * Discriminated union type for multi-feed sub-channel data
 * Each sub-channel has its own specific data structure
 */
export type MultiFeedSubChannelData =
  | { subChannel: "orderbook"; data: MultiFeedOrderbookData }
  | { subChannel: "trades"; data: MultiFeedTradesData }
  | { subChannel: "positions"; data: MultiFeedPositionsData }
  | { subChannel: "orders"; data: MultiFeedOrdersData }
  | { subChannel: "buckets"; data: MultiFeedBucketsData }
  | { subChannel: "balances"; data: MultiFeedBalancesData }
  | { subChannel: "markPrice"; data: MultiFeedMarkPriceData }
  | { subChannel: "fundingRate"; data: MultiFeedFundingRateData }
  | { subChannel: "deviation"; data: MultiFeedDeviationData }
  | { subChannel: "twap"; data: MultiFeedTWAPData }
  | { subChannel: "kline"; data: MultiFeedKlineData }
  | { subChannel: "lastPrice"; data: MultiFeedLastPriceData }
  | { subChannel: "indexPrice"; data: MultiFeedIndexPriceData }
  | { subChannel: "priceFeed"; data: MultiFeedPriceFeedData }
  | { subChannel: string; data: unknown };

/**
 * Type guard to check if a message is a multi-feed orderbook message (proto camelCase).
 */
export function isMultiFeedOrderbook(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "orderbook";
  data: MultiFeedOrderbookData;
} {
  return (message as StreamMessageEnvelope).subChannel === "orderbook";
}

/**
 * Type guard to check if a message is a multi-feed trades message
 */
export function isMultiFeedTrades(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "trades";
  data: MultiFeedTradesData;
} {
  return (message as StreamMessageEnvelope).subChannel === "trades";
}

/**
 * Type guard to check if a message is a multi-feed positions message
 */
export function isMultiFeedPositions(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "positions";
  data: MultiFeedPositionsData;
} {
  return (message as StreamMessageEnvelope).subChannel === "positions";
}

/**
 * Type guard to check if a message is a multi-feed orders message
 */
export function isMultiFeedOrders(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "orders";
  data: MultiFeedOrdersData;
} {
  return (message as StreamMessageEnvelope).subChannel === "orders";
}

/**
 * Type guard to check if a message is a multi-feed buckets message
 */
export function isMultiFeedBuckets(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "buckets";
  data: MultiFeedBucketsData;
} {
  return (message as StreamMessageEnvelope).subChannel === "buckets";
}

/**
 * Type guard to check if a message is a multi-feed balances message
 */
export function isMultiFeedBalances(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "balances";
  data: MultiFeedBalancesData;
} {
  return (message as StreamMessageEnvelope).subChannel === "balances";
}

/** Type guard for multi-feed mark price message (sub_channel "markPrice"). */
export function isMultiFeedMarkPrice(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "markPrice";
  data: MultiFeedMarkPriceData;
} {
  return (message as StreamMessageEnvelope).subChannel === "markPrice";
}

/** Type guard for multi-feed funding rate message (sub_channel "fundingRate"). */
export function isMultiFeedFundingRate(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "fundingRate";
  data: MultiFeedFundingRateData;
} {
  return (message as StreamMessageEnvelope).subChannel === "fundingRate";
}

/**
 * Type guard to check if a message is a multi-feed deviation message
 */
export function isMultiFeedDeviation(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "deviation";
  data: MultiFeedDeviationData;
} {
  return (message as StreamMessageEnvelope).subChannel === "deviation";
}

/**
 * Type guard to check if a message is a multi-feed TWAP message
 */
export function isMultiFeedTWAP(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "twap";
  data: MultiFeedTWAPData;
} {
  return (message as StreamMessageEnvelope).subChannel === "twap";
}

/**
 * Type guard to check if a message is a multi-feed kline message
 */
export function isMultiFeedKline(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "kline";
  data: MultiFeedKlineData;
} {
  return (message as StreamMessageEnvelope).subChannel === "kline";
}

/** Type guard for multi-feed last price message (sub_channel "lastPrice"). */
export function isMultiFeedLastPrice(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "lastPrice";
  data: MultiFeedLastPriceData;
} {
  return (message as StreamMessageEnvelope).subChannel === "lastPrice";
}

/** Type guard for multi-feed index price message (sub_channel "indexPrice"). */
export function isMultiFeedIndexPrice(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "indexPrice";
  data: MultiFeedIndexPriceData;
} {
  return (message as StreamMessageEnvelope).subChannel === "indexPrice";
}

/**
 * Type guard to check if a message is a multi-feed price feed message
 */
export function isMultiFeedPriceFeed(
  message: StreamMessage,
): message is StreamMessageEnvelope & {
  subChannel: "priceFeed";
  data: MultiFeedPriceFeedData;
} {
  return (message as StreamMessageEnvelope).subChannel === "priceFeed";
}

// ============================================================================
// UNIFIED MULTIPLEX STREAM API (channel "multiplex" + action/subscriptions)
// Mirrors orchestrator e2e_tests/stream and router/websocket/multiplex/types.go
// ============================================================================

/** Stream type identifiers for the multiplex API (canonical; matches E2E and router). */
export type MultiplexStreamType =
  | "priceFeed"
  | "volatility"
  | "indexPrice"
  | "markPrice"
  | "fundingRate"
  | "deviation"
  | "orderbook"
  | "trades"
  | "positions"
  | "transactions";

/** Action for multiplex requests. */
export type MultiplexAction = "subscribe" | "unsubscribe" | "ping";

/**
 * Stream filter (wire format). Matches orchestrator e2e_tests/stream StreamFilter and router multiplex.
 * Only relevant fields per stream_type are used. Keys match JSON wire (camelCase/snake as in E2E).
 */
export interface MultiplexStreamFilter {
  symbols?: string[];
  marketIndices?: number[];
  include_all?: boolean;
  updateIntervalMs?: number;
  includeSnapshot?: boolean;
  sources?: string[];
  includeAggregated?: boolean;
  depth?: number;
  address?: string;
  txTypes?: string[];
  shardId?: string;
  /** For deviation stream: "last_vsIndex" | "mark_vsIndex" */
  deviation_method?: string;
}

/** Single stream subscription in a multiplex request. */
export interface MultiplexStreamSubscription {
  stream_type: MultiplexStreamType;
  filter?: MultiplexStreamFilter;
}

/** Client → server: subscribe/unsubscribe streams or ping. */
export interface MultiplexRequest {
  action: MultiplexAction;
  id?: string;
  subscriptions?: MultiplexStreamSubscription[];
}

/** Server → client: ack, data, error, pong. */
export interface MultiplexResponse {
  type: "ack" | "data" | "error" | "pong";
  id?: string;
  stream_type?: MultiplexStreamType;
  symbol?: string;
  marketIndex?: number;
  data?: unknown;
  error?: MultiplexErrorDetail;
  U?: number;
  u?: number;
  ts?: number;
}

export interface MultiplexErrorDetail {
  code: string;
  message: string;
  details?: string;
}

/** Callback for multiplex stream data (one per stream_type). */
export type MultiplexDataCallback = (resp: MultiplexResponse) => void;
